import React, { useMemo } from 'react';
import { Row, Col, FormGroup, Label, Input, Button } from 'reactstrap';
import Select from 'components/Select';
import PercentageInput from '../PercentageInput';
import { useUpdateSalesLocation } from '../../api/sales-locations/service';
import { useGetChannels } from '../../api/channels/service';
import { toNumber } from 'utils/currency';
import { getActiveColorStyle } from 'utils/colorUtils';
import { getTheme2FormPageDarkSelectStyles } from 'utils/theme2MultiSelect';
import {
    SALES_GROUP_PERSON_PAGE_SIZE,
    getSalesPersonOptions,
    getFiPersonOptions,
} from 'utils/salesGroupPersonOptions';

const LeadDefaults = ({ formData, activeColor, isTheme2 = false, salesLocationId, onSave }) => {
    const activeColorStyle = useMemo(() => getActiveColorStyle(activeColor), [activeColor]);
    const selectStyles = useMemo(
        () => getTheme2FormPageDarkSelectStyles(activeColorStyle, isTheme2),
        [activeColorStyle, isTheme2]
    );
    // Safety check - if formData is undefined, provide default values
    const safeFormData = formData || {};
    const updateSalesLocationMutation = useUpdateSalesLocation();

    // Fetch sales group persons for dropdowns
    const { data: salesGroupPersons } = useGetChannels("sales_group_person", 1, SALES_GROUP_PERSON_PAGE_SIZE);
    const salesGroupPersonResults = salesGroupPersons?.results || [];

    const salesRoleOptions = React.useMemo(() => getSalesPersonOptions(salesGroupPersonResults), [salesGroupPersonResults]);

    const fiRoleOptions = React.useMemo(() => getFiPersonOptions(salesGroupPersonResults), [salesGroupPersonResults]);

    // Local state for form data
    const [localFormData, setLocalFormData] = React.useState(formData || {});

    // Update local state when formData prop changes
    React.useEffect(() => {
        setLocalFormData(formData || {});
    }, [formData]);

    const saveLeadDefaultsRef = React.useRef();

    // Function to save lead defaults data
    const saveLeadDefaults = React.useCallback(() => {
        if (salesLocationId && localFormData) {
            const updateData = {
                lead_defaults: {
                    lead_source: localFormData.lead_source || null,
                    sales_person_1: localFormData.sales_person_1 || null,
                    sales_person_2: localFormData.sales_person_2 || null,
                    fi_person: localFormData.fi_person || null,
                    closing_probability: toNumber(localFormData.closing_probability) || 50
                }
            };

            updateSalesLocationMutation.mutate({
                id: salesLocationId,
                data: updateData
            });
        }
    }, [salesLocationId, localFormData, updateSalesLocationMutation]);

    // Update ref whenever saveLeadDefaults changes
    React.useEffect(() => {
        saveLeadDefaultsRef.current = saveLeadDefaults;
    }, [saveLeadDefaults]);

    // Call onSave callback when component mounts to register save function
    React.useEffect(() => {
        if (onSave) {
            onSave('LEAD_DEFAULTS', () => saveLeadDefaultsRef.current?.());
        }
    }, [onSave]);

    return (
        <div className="">
            {/* LEAD SOURCE */}
            <h5 className="mb-3">LEAD SOURCE</h5>
            <Row>
                <Col md="6">
                    <FormGroup>
                        <Label className='form-control-label'>Lead Source:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.lead_source ? { value: localFormData.lead_source, label: localFormData.lead_source } : null}
                            onChange={(option) => {
                                const value = option ? option.value : '';
                                setLocalFormData(prev => ({ ...prev, lead_source: value }));
                            }}
                            options={[
                                { value: 'Deal Manager', label: 'Deal Manager' },
                                { value: 'Client Web Site', label: 'Client Web Site' },
                                { value: 'Autobing', label: 'Autobing' },
                            ]}
                            placeholder="Select Lead Source"
                        />
                    </FormGroup>
                </Col>
            </Row>

            {/* DEFAULT LEAD CREATION SETTINGS */}
            <h5 className="mb-3 mt-4">DEFAULT LEAD CREATION SETTINGS</h5>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Sales Person 1:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.sales_person_1 ? {
                                value: localFormData.sales_person_1,
                                label: salesRoleOptions.find(p => p.id === Number(localFormData.sales_person_1))?.name || 'Unknown'
                            } : null}
                            onChange={(option) => {
                                const value = option ? option.value : '';
                                setLocalFormData(prev => ({ ...prev, sales_person_1: value }));
                            }}
                            options={salesRoleOptions.map(p => ({ value: p.id, label: p.name }))}
                            placeholder="Select Sales Person"
                            isClearable
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Sales Person 2:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.sales_person_2 ? {
                                value: localFormData.sales_person_2,
                                label: salesRoleOptions.find(p => p.id === Number(localFormData.sales_person_2))?.name || 'Unknown'
                            } : null}
                            onChange={(option) => {
                                const value = option ? option.value : '';
                                setLocalFormData(prev => ({ ...prev, sales_person_2: value }));
                            }}
                            options={salesRoleOptions.map(p => ({ value: p.id, label: p.name }))}
                            placeholder="Select Sales Person"
                            isClearable
                        />
                    </FormGroup>
                </Col>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>F & I Person:</Label>
                        <Select
                            activeColor={activeColor}
                            styles={selectStyles}
                            className="react-select"
                            value={localFormData.fi_person ? {
                                value: localFormData.fi_person,
                                label: fiRoleOptions.find(p => p.id === Number(localFormData.fi_person))?.name || 'Unknown'
                            } : null}
                            onChange={(option) => {
                                const value = option ? option.value : '';
                                setLocalFormData(prev => ({ ...prev, fi_person: value }));
                            }}
                            options={fiRoleOptions.map(p => ({ value: p.id, label: p.name }))}
                            placeholder="Select F & I Person"
                            isClearable
                        />
                    </FormGroup>
                </Col>
            </Row>
            <Row>
                <Col md="4">
                    <FormGroup>
                        <Label className='form-control-label'>Closing Probability:</Label>
                        <PercentageInput
                            value={localFormData.closing_probability || "50"}
                            onChange={(value) => {
                                setLocalFormData(prev => ({ ...prev, closing_probability: value }));
                            }}
                            decimals={0}
                            maxValue={100}
                        />
                    </FormGroup>
                </Col>
            </Row>


        </div>
    );
};

export default LeadDefaults;
